<template>
  <div class="event-page-layout">

    <!-- =========================================================
         HEADER
    ========================================================== -->

    <DefaultHeader
      :title="event?.title || 'Ивент'"
      subtitle="Описание ивента"
      :offset="650"
       class="event-header-fixed"
    />


    <!-- =========================================================
         CONTENT
    ========================================================== -->

    <main class="event-scrollable-content">

      <EventSkeleton
        v-if="loading"
      />


      <div
        v-else-if="event"
        class="event-container"
      >

        <!-- =====================================================
             HERO
        ====================================================== -->

        <section class="event-hero">

          <img
            v-if="event.imageURL"
            :src="event.imageURL"
            :alt="event.title || 'Ивент'"
            class="event-image"
          />


          <div
            v-else
            class="event-image-placeholder"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
            >
              <rect
                x="4"
                y="5"
                width="16"
                height="15"
                rx="3"
                stroke="currentColor"
                stroke-width="1.7"
              />

              <path
                d="M8 3V7M16 3V7M4 9H20"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />
            </svg>
          </div>


          <div class="hero-gradient"></div>


          <!-- DATE -->

          <div class="hero-date">

            <template v-if="parsedEventDate">

              <strong>
                {{ eventDay }}
              </strong>

              <span>
                {{ eventMonth }}
              </span>

            </template>

            <template v-else>
              <span class="date-fallback">
                {{ event.dateOfEvent }}
              </span>
            </template>

          </div>


          <!-- LOCATION -->

          <div
            v-if="regionName"
            class="hero-location"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 21C12 21 18 15.8 18 10.5C18 6.91 15.31 4 12 4C8.69 4 6 6.91 6 10.5C6 15.8 12 21 12 21Z"
                stroke="currentColor"
                stroke-width="1.6"
              />

              <circle
                cx="12"
                cy="10"
                r="2"
                stroke="currentColor"
                stroke-width="1.6"
              />
            </svg>

            {{ regionName }}
          </div>


          <!-- HERO CONTENT -->

          <div class="hero-content">

            <span class="hero-label">
              Ивент
            </span>

            <h1>
              {{ event.title || "Без названия" }}
            </h1>

            <p
              v-if="event.previewTitle"
              class="hero-preview-title"
            >
              {{ event.previewTitle }}
            </p>

          </div>

        </section>


        <!-- =====================================================
             BODY GRID
        ====================================================== -->

        <div class="event-layout">


          <!-- ===================================================
               MAIN CONTENT
          ==================================================== -->

          <section class="event-main">

            <!-- ORGANIZER -->

            <div
              v-if="event.companyName"
              class="organizer-card"
            >

              <div class="organizer-logo">

                <img
                  v-if="event.companyLogoURL"
                  :src="event.companyLogoURL"
                  :alt="event.companyName"
                />

                <span v-else>
                  {{ companyInitial }}
                </span>

              </div>


              <div class="organizer-content">

                <span class="organizer-label">
                  Организатор
                </span>

                <strong>
                  {{ event.companyName }}
                </strong>

                <p
                  v-if="event.companyAbout"
                >
                  {{ event.companyAbout }}
                </p>

              </div>


              <div class="organizer-mark">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M5 20V8L12 4L19 8V20"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />

                  <path
                    d="M9 20V14H15V20"
                    stroke="currentColor"
                    stroke-width="1.6"
                  />
                </svg>
              </div>

            </div>


            <!-- ABOUT -->

            <div class="content-section">

              <div class="section-heading">

                <span class="section-kicker">
                  О событии
                </span>

                <h2>
                  Подробнее
                </h2>

              </div>


              <p class="event-description">
                {{
                  event.description
                  || "Описание события отсутствует."
                }}
              </p>

            </div>


            <!-- PARTICIPANTS -->

            <div class="content-section participants-section">

              <div class="section-heading">

                <span class="section-kicker">
                  Сообщество
                </span>

                <h2>
                  Участники
                </h2>

              </div>


              <div
                v-if="event.participants?.length"
                class="participants-card"
              >

                <div class="avatars">

                  <div
                    v-for="(participant, index) in visibleParticipants"
                    :key="participant.user_id || index"
                    class="avatar-wrapper"
                    :style="{
                      zIndex:
                        visibleParticipants.length - index
                    }"
                  >

                    <img
                      v-if="participant.image_url"
                      :src="participant.image_url"
                      class="avatar"
                      alt=""
                    />

                    <span v-else>
                      ?
                    </span>

                  </div>

                </div>


                <div class="participants-info">

                  <strong>
                    {{ event.participants.length }}
                  </strong>

                  <span>
                    {{ participantsTitle }}
                    уже отметились
                  </span>

                </div>

              </div>


              <div
                v-else
                class="participants-card empty"
              >

                <div class="empty-participants-icon">

                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      cx="12"
                      cy="8"
                      r="3"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />

                    <path
                      d="M6 19C6 15.7 8.7 13 12 13C15.3 13 18 15.7 18 19"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                    />
                  </svg>

                </div>

                <div>
                  <strong>
                    Пока никто не отметился
                  </strong>

                  <span>
                    Будьте одним из первых участников события
                  </span>
                </div>

              </div>

            </div>

          </section>


          <!-- ===================================================
               SIDEBAR
          ==================================================== -->

          <aside class="event-sidebar">

            <div class="sidebar-sticky">


              <!-- INFO -->

              <div class="info-card">

                <span class="info-card-label">
                  Информация
                </span>


                <!-- DATE -->

                <div class="info-row">

                  <div class="info-icon date">

                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <rect
                        x="4"
                        y="5"
                        width="16"
                        height="15"
                        rx="3"
                        stroke="currentColor"
                        stroke-width="1.6"
                      />

                      <path
                        d="M8 3V7M16 3V7M4 9H20"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round"
                      />
                    </svg>

                  </div>


                  <div>
                    <span>
                      Дата
                    </span>

                    <strong>
                      {{ formattedEventDate }}
                    </strong>
                  </div>

                </div>


                <!-- LOCATION -->

                <div
                  v-if="regionName"
                  class="info-row"
                >

                  <div class="info-icon location">

                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M12 21C12 21 18 15.8 18 10.5C18 6.91 15.31 4 12 4C8.69 4 6 6.91 6 10.5C6 15.8 12 21 12 21Z"
                        stroke="currentColor"
                        stroke-width="1.6"
                      />

                      <circle
                        cx="12"
                        cy="10"
                        r="2"
                        stroke="currentColor"
                        stroke-width="1.6"
                      />
                    </svg>

                  </div>


                  <div>
                    <span>
                      Город
                    </span>

                    <strong>
                      {{ regionName }}
                    </strong>
                  </div>

                </div>


                <!-- ORGANIZER -->

                <div
                  v-if="event.companyName"
                  class="info-row"
                >

                  <div class="info-icon company">

                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M5 20V8L12 4L19 8V20"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />

                      <path
                        d="M9 20V14H15V20"
                        stroke="currentColor"
                        stroke-width="1.6"
                      />
                    </svg>

                  </div>


                  <div>
                    <span>
                      Организатор
                    </span>

                    <strong>
                      {{ event.companyName }}
                    </strong>
                  </div>

                </div>

              </div>


              <!-- CTA -->

              <div class="action-card">

                <div class="action-heading">

                  <div class="action-icon">
                    ↗
                  </div>

                  <div>

                    <strong>
                      Перейти к событию
                    </strong>

                    <span>
                      Откроется сайт организатора
                    </span>

                  </div>

                </div>


                <button
                  type="button"
                  class="event-button"
                  :disabled="!event.linkURL"
                  @click="openExternalLink"
                >

                  <span>
                    Открыть страницу ивента
                  </span>


                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M7 5L12 10L7 15"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                </button>

              </div>

            </div>

          </aside>

        </div>

      </div>


      <!-- =====================================================
           ERROR / NO EVENT
      ====================================================== -->

      <div
        v-else
        class="error-state"
      >

        <div class="error-icon">
          !
        </div>

        <h2>
          Ивент не найден
        </h2>

        <p>
          Возможно, событие было удалено
          или больше недоступно.
        </p>

      </div>

    </main>


    <!-- =========================================================
         FOOTER
    ========================================================== -->

    <Footer
      class="feed-footer"
    />

  </div>
</template>


<script setup>

import {
  getAuth,
  onAuthStateChanged
} from "firebase/auth";

import {
  computed,
  onMounted,
  ref
} from "vue";

import {
  useRoute
} from "vue-router";

import {
  AnalyticsService,
  AnalyticsEvent
} from "../utils/AnalyticsService";

import DefaultHeader
  from "../components/EventHeader.vue";

import Footer
  from "../components/MiniFooter.vue";

import RegionService
  from "../utils/RegionService.js";

import EventService
  from "../api/EventService.js";

import EventSkeleton
  from "../components/EventSkeleton.vue";


const route =
  useRoute();


const loading =
  ref(true);

const event =
  ref(null);


/* ============================================================
   LOAD
============================================================ */

onMounted(() => {
  const auth = getAuth();

  onAuthStateChanged(
    auth,
    async user => {
      if (!user) {
        console.warn(
          "❌ Пользователь не авторизован → редирект на стартовую"
        );

        window.location.replace(
          "https://hr3.space/"
        );

        return;
      }

      try {
        /*
         * Если пришли из списка событий,
         * используем state без лишнего запроса.
         */
        if (
          history.state?.event
        ) {
          event.value =
            history.state.event;

          AnalyticsService.log(
            AnalyticsEvent.VIEW_EVENT
          );

          return;
        }

        event.value =
          await EventService
            .fetchEventById(
              route.params.eventId
            );

        AnalyticsService.log(
          AnalyticsEvent.VIEW_EVENT
        );
      } catch (error) {
        console.error(
          "❌ Ошибка загрузки ивента:",
          error
        );
      } finally {
        loading.value =
          false;
      }
    }
  );
});

/* ============================================================
   REGION
============================================================ */

const regionName =
  computed(() => {

    if (
      !event.value
      || event.value.cityID === null
      || event.value.cityID === undefined
      || event.value.cityID === ""
    ) {
      return "";
    }


    return (
      RegionService.handleRegion(
        Number(
          event.value.cityID
        )
      )
      || ""
    );

  });


/* ============================================================
   DATE
============================================================ */

const parsedEventDate =
  computed(() => {

    const value =
      event.value?.dateOfEvent;


    if (
      value === null
      || value === undefined
      || value === ""
    ) {
      return null;
    }


    const numericValue =
      Number(value);


    if (
      !Number.isNaN(
        numericValue
      )
    ) {

      const milliseconds =
        numericValue < 100000000000
          ? numericValue * 1000
          : numericValue;


      const date =
        new Date(
          milliseconds
        );


      if (
        !Number.isNaN(
          date.getTime()
        )
      ) {
        return date;
      }

    }


    const date =
      new Date(value);


    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return null;
    }


    return date;

  });


const eventDay =
  computed(() => {

    if (
      !parsedEventDate.value
    ) {
      return "";
    }


    return new Intl.DateTimeFormat(
      "ru-RU",
      {
        day: "2-digit"
      }
    ).format(
      parsedEventDate.value
    );

  });


const eventMonth =
  computed(() => {

    if (
      !parsedEventDate.value
    ) {
      return "";
    }


    return new Intl.DateTimeFormat(
      "ru-RU",
      {
        month: "short"
      }
    )
      .format(
        parsedEventDate.value
      )
      .replace(
        ".",
        ""
      )
      .toUpperCase();

  });


const formattedEventDate =
  computed(() => {

    if (
      !parsedEventDate.value
    ) {
      return (
        event.value?.dateOfEvent
        || "Дата не указана"
      );
    }


    return new Intl.DateTimeFormat(
      "ru-RU",
      {
        day: "numeric",
        month: "long",
        year: "numeric"
      }
    ).format(
      parsedEventDate.value
    );

  });


/* ============================================================
   COMPANY
============================================================ */

const companyInitial =
  computed(() => {

    const name =
      event.value?.companyName;


    if (!name) {
      return "?";
    }


    return name
      .trim()
      .charAt(0)
      .toUpperCase();

  });


/* ============================================================
   PARTICIPANTS
============================================================ */

const visibleParticipants =
  computed(() => {

    if (
      !Array.isArray(
        event.value?.participants
      )
    ) {
      return [];
    }


    return event.value.participants
      .slice(
        0,
        5
      );

  });


const participantsTitle =
  computed(() => {

    const count =
      event.value
        ?.participants
        ?.length
      || 0;


    const mod10 =
      count % 10;

    const mod100 =
      count % 100;


    if (
      mod10 === 1
      && mod100 !== 11
    ) {
      return "участник";
    }


    if (
      mod10 >= 2
      && mod10 <= 4
      && (
        mod100 < 12
        || mod100 > 14
      )
    ) {
      return "участника";
    }


    return "участников";

  });


/* ============================================================
   LINK
============================================================ */

const openExternalLink =
  () => {

    if (
      !event.value?.linkURL
    ) {
      return;
    }


    AnalyticsService.log(
      AnalyticsEvent.OPEN_EVENT_LINK
    );


    window.open(
      event.value.linkURL,
      "_blank",
      "noopener,noreferrer"
    );

  };

</script>


<style scoped>

* {
  box-sizing: border-box;
}


/* ============================================================
   PAGE
============================================================ */

.event-page-layout {
  width: 100%;
  height: 100dvh;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  color: #24272d;

  background: #f4f6f9;
}


.event-header-fixed,
.feed-footer {
  flex-shrink: 0;
}


.event-scrollable-content {
  flex: 1;
  min-height: 0;

  overflow-y: auto;
  overflow-x: hidden;

  padding:
    42px
    24px
    70px;

  scrollbar-width: none;

  background:
    radial-gradient(
      circle at 12% 0%,
      rgba(40, 135, 247, 0.075),
      transparent 390px
    ),
    radial-gradient(
      circle at 90% 9%,
      rgba(124, 92, 231, 0.055),
      transparent 360px
    ),
    #f4f6f9;
}


.event-scrollable-content::-webkit-scrollbar {
  display: none;
}


.event-container {
  width: 100%;
  max-width: 1100px;

  margin: 0 auto;
}


/* ============================================================
   HERO
============================================================ */

.event-hero {
  position: relative;

  width: 100%;
  height: 500px;

  overflow: hidden;

  background: #e9edf3;

  border-radius: 26px;

  box-shadow:
    0 18px 50px
    rgba(
      24,
      35,
      55,
      0.12
    );
}


.event-image {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
}


.event-image-placeholder {
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #aab1bc;

  background:
    linear-gradient(
      135deg,
      #eef1f6,
      #e1e6ee
    );
}


.event-image-placeholder svg {
  width: 56px;
  height: 56px;
}


.hero-gradient {
  position: absolute;

  inset: 0;

  background:
    linear-gradient(
      to top,
      rgba(
        8,
        13,
        23,
        0.78
      )
      0%,
      rgba(
        8,
        13,
        23,
        0.26
      )
      48%,
      rgba(
        8,
        13,
        23,
        0.05
      )
      100%
    );

  pointer-events: none;
}


/* ============================================================
   HERO CONTENT
============================================================ */

.hero-content {
  position: absolute;

  left: 38px;
  right: 130px;
  bottom: 36px;

  z-index: 2;
}


.hero-label {
  display: block;

  margin-bottom: 8px;

  color:
    rgba(
      255,
      255,
      255,
      0.7
    );

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  letter-spacing: 0.1em;

  text-transform: uppercase;
}


.hero-content h1 {
  max-width: 780px;

  margin: 0;

  color: #ffffff;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 38px;
  line-height: 1.12;

  text-shadow:
    0 4px 20px
    rgba(
      0,
      0,
      0,
      0.18
    );
}


.hero-preview-title {
  max-width: 650px;

  margin:
    10px
    0
    0;

  color:
    rgba(
      255,
      255,
      255,
      0.74
    );

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 13px;
  line-height: 1.5;
}


/* ============================================================
   HERO DATE
============================================================ */

.hero-date {
  position: absolute;

  top: 22px;
  right: 22px;

  z-index: 3;

  min-width: 64px;
  height: 64px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  padding:
    0
    9px;

  color: #24282f;

  background:
    rgba(
      255,
      255,
      255,
      0.94
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.6
    );

  border-radius: 18px;

  box-shadow:
    0 10px 24px
    rgba(
      0,
      0,
      0,
      0.14
    );

  backdrop-filter:
    blur(14px);

  -webkit-backdrop-filter:
    blur(14px);
}


.hero-date strong {
  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 22px;
  line-height: 1;
}


.hero-date span:not(.date-fallback) {
  margin-top: 4px;

  color: #727985;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  letter-spacing: 0.06em;
}


.date-fallback {
  max-width: 85px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  text-align: center;
}


/* ============================================================
   HERO LOCATION
============================================================ */

.hero-location {
  position: absolute;

  top: 23px;
  left: 22px;

  z-index: 3;

  display: inline-flex;
  align-items: center;

  gap: 6px;

  padding:
    8px
    11px;

  color: #ffffff;

  background:
    rgba(
      18,
      23,
      31,
      0.5
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.14
    );

  border-radius: 11px;

  backdrop-filter:
    blur(14px);

  -webkit-backdrop-filter:
    blur(14px);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


.hero-location svg {
  width: 14px;
  height: 14px;
}


/* ============================================================
   LAYOUT
============================================================ */

.event-layout {
  display: grid;

  grid-template-columns:
    minmax(
      0,
      1fr
    )
    320px;

  gap: 22px;

  align-items: start;

  margin-top: 22px;
}


/* ============================================================
   MAIN
============================================================ */

.event-main {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 18px;
}


/* ============================================================
   ORGANIZER
============================================================ */

.organizer-card {
  position: relative;

  display: flex;
  align-items: flex-start;

  gap: 14px;

  padding: 19px;

  background:
    rgba(
      255,
      255,
      255,
      0.94
    );

  border:
    1px solid
    rgba(
      31,
      44,
      62,
      0.065
    );

  border-radius: 18px;

  box-shadow:
    0 10px 28px
    rgba(
      15,
      23,
      42,
      0.04
    );
}


.organizer-logo {
  width: 54px;
  height: 54px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  color: #677da0;

  background:
    linear-gradient(
      135deg,
      #e8eef7,
      #f1f3f7
    );

  border-radius: 15px;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 18px;
}


.organizer-logo img {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
}


.organizer-content {
  min-width: 0;

  flex: 1;
}


.organizer-label {
  display: block;

  margin-bottom: 3px;

  color: #9fa5ae;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  letter-spacing: 0.07em;

  text-transform: uppercase;
}


.organizer-content strong {
  color: #373c44;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 14px;
}


.organizer-content p {
  display: -webkit-box;

  max-width: 630px;

  margin:
    5px
    0
    0;

  overflow: hidden;

  color: #858c96;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;
  line-height: 1.5;

  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}


.organizer-mark {
  width: 34px;
  height: 34px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #347fe7;

  background:
    rgba(
      52,
      127,
      231,
      0.07
    );

  border-radius: 10px;
}


.organizer-mark svg {
  width: 16px;
  height: 16px;
}


/* ============================================================
   CONTENT SECTION
============================================================ */

.content-section {
  padding: 27px;

  background:
    rgba(
      255,
      255,
      255,
      0.94
    );

  border:
    1px solid
    rgba(
      31,
      44,
      62,
      0.065
    );

  border-radius: 20px;

  box-shadow:
    0 10px 28px
    rgba(
      15,
      23,
      42,
      0.04
    );
}


.section-heading {
  margin-bottom: 18px;
}


.section-kicker {
  display: block;

  margin-bottom: 4px;

  color: #9ba1aa;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  letter-spacing: 0.08em;

  text-transform: uppercase;
}


.section-heading h2 {
  margin: 0;

  color: #30353c;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 18px;
}


.event-description {
  margin: 0;

  color: #626a75;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 14px;
  line-height: 1.75;

  white-space: pre-line;
}


/* ============================================================
   PARTICIPANTS
============================================================ */

.participants-card {
  display: flex;
  align-items: center;

  gap: 14px;

  padding:
    15px
    16px;

  background: #f7f8fa;

  border:
    1px solid
    rgba(
      31,
      44,
      62,
      0.055
    );

  border-radius: 15px;
}


.avatars {
  display: flex;

  flex-shrink: 0;

  padding-left: 2px;
}


.avatar-wrapper {
  width: 38px;
  height: 38px;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  margin-left: -9px;

  color: #87909b;

  background: #e7ebf0;

  border:
    3px solid
    #f7f8fa;

  border-radius: 50%;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 9px;
}


.avatar-wrapper:first-child {
  margin-left: 0;
}


.avatar {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
}


.participants-info {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 2px;
}


.participants-info strong {
  color: #444a53;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 12px;
}


.participants-info span {
  color: #9097a1;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;
}


.participants-card.empty {
  gap: 11px;
}


.empty-participants-icon {
  width: 38px;
  height: 38px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #9299a3;

  background: #eceff3;

  border-radius: 11px;
}


.empty-participants-icon svg {
  width: 17px;
  height: 17px;
}


.participants-card.empty strong {
  display: block;

  color: #555c65;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
}


.participants-card.empty span {
  display: block;

  margin-top: 2px;

  color: #969da7;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;
}


/* ============================================================
   SIDEBAR
============================================================ */

.sidebar-sticky {
  position: sticky;

  top: 10px;

  display: flex;
  flex-direction: column;

  gap: 15px;
}


/* ============================================================
   INFO CARD
============================================================ */

.info-card,
.action-card {
  padding: 18px;

  background:
    rgba(
      255,
      255,
      255,
      0.94
    );

  border:
    1px solid
    rgba(
      31,
      44,
      62,
      0.065
    );

  border-radius: 18px;

  box-shadow:
    0 10px 28px
    rgba(
      15,
      23,
      42,
      0.04
    );
}


.info-card-label {
  display: block;

  margin-bottom: 14px;

  color: #999fa9;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  letter-spacing: 0.08em;

  text-transform: uppercase;
}


.info-row {
  display: flex;
  align-items: center;

  gap: 10px;

  padding:
    11px
    0;

  border-top:
    1px solid
    rgba(
      31,
      44,
      62,
      0.055
    );
}


.info-row:first-of-type {
  border-top: none;

  padding-top: 0;
}


.info-row:last-child {
  padding-bottom: 0;
}


.info-icon {
  width: 34px;
  height: 34px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 10px;
}


.info-icon svg {
  width: 16px;
  height: 16px;
}


.info-icon.date {
  color: #775fd0;

  background:
    rgba(
      119,
      95,
      208,
      0.08
    );
}


.info-icon.location {
  color: #347fe7;

  background:
    rgba(
      52,
      127,
      231,
      0.08
    );
}


.info-icon.company {
  color: #31a273;

  background:
    rgba(
      49,
      162,
      115,
      0.08
    );
}


.info-row > div:last-child {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 2px;
}


.info-row span {
  color: #9aa1aa;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 7px;
}


.info-row strong {
  overflow: hidden;

  color: #454b54;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  text-overflow: ellipsis;
}


/* ============================================================
   ACTION
============================================================ */

.action-heading {
  display: flex;
  align-items: center;

  gap: 10px;

  margin-bottom: 14px;
}


.action-icon {
  width: 36px;
  height: 36px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #2887f7,
      #486de6
    );

  border-radius: 11px;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 13px;
}


.action-heading > div:last-child {
  display: flex;
  flex-direction: column;

  gap: 2px;
}


.action-heading strong {
  color: #4b515a;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
}


.action-heading span {
  color: #979da6;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 7px;
}


.event-button {
  width: 100%;
  min-height: 43px;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  padding:
    0
    14px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #2887f7,
      #486de6
    );

  border: none;
  border-radius: 12px;

  box-shadow:
    0 8px 18px
    rgba(
      40,
      135,
      247,
      0.18
    );

  cursor: pointer;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  transition:
    transform
    0.18s ease,
    box-shadow
    0.18s ease,
    opacity
    0.18s ease;
}


.event-button:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0 11px 23px
    rgba(
      40,
      135,
      247,
      0.27
    );
}


.event-button:disabled {
  cursor: default;

  opacity: 0.38;

  box-shadow: none;
}


.event-button svg {
  width: 14px;
  height: 14px;
}


/* ============================================================
   ERROR
============================================================ */

.error-state {
  max-width: 480px;

  margin:
    90px
    auto;

  padding:
    40px
    30px;

  text-align: center;

  background: #ffffff;

  border-radius: 20px;
}


.error-icon {
  width: 50px;
  height: 50px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin:
    0
    auto
    15px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #e76b68,
      #ca4d54
    );

  border-radius: 15px;

  font-family:
    "Eastman-Bold",
    sans-serif;
}


.error-state h2 {
  margin: 0;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 18px;
}


.error-state p {
  margin:
    8px
    0
    0;

  color: #858c96;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.event-page-layout {
  color: #f2f2f4;

  background: #18191c;
}


html[data-theme="dark"]
.event-scrollable-content {
  background:
    radial-gradient(
      circle at 12% 0%,
      rgba(40, 135, 247, 0.06),
      transparent 390px
    ),
    radial-gradient(
      circle at 90% 9%,
      rgba(124, 92, 231, 0.055),
      transparent 360px
    ),
    #18191c;
}


html[data-theme="dark"]
.organizer-card,
html[data-theme="dark"]
.content-section,
html[data-theme="dark"]
.info-card,
html[data-theme="dark"]
.action-card,
html[data-theme="dark"]
.error-state {
  background: #232428;

  border-color:
    rgba(
      255,
      255,
      255,
      0.065
    );

  box-shadow:
    0 12px 30px
    rgba(
      0,
      0,
      0,
      0.2
    );
}


html[data-theme="dark"]
.organizer-logo {
  color: #a2aec0;

  background:
    linear-gradient(
      135deg,
      #30333a,
      #383b42
    );
}


html[data-theme="dark"]
.organizer-content strong,
html[data-theme="dark"]
.section-heading h2,
html[data-theme="dark"]
.info-row strong,
html[data-theme="dark"]
.action-heading strong,
html[data-theme="dark"]
.error-state h2 {
  color: #eeeeef;
}


html[data-theme="dark"]
.organizer-content p,
html[data-theme="dark"]
.event-description {
  color: #a0a6af;
}


html[data-theme="dark"]
.participants-card {
  background: #2d2f34;

  border-color:
    rgba(
      255,
      255,
      255,
      0.055
    );
}


html[data-theme="dark"]
.avatar-wrapper {
  color: #a0a7b0;

  background: #393c42;

  border-color: #2d2f34;
}


html[data-theme="dark"]
.participants-info strong,
html[data-theme="dark"]
.participants-card.empty strong {
  color: #dfe1e4;
}


html[data-theme="dark"]
.empty-participants-icon {
  color: #949ba5;

  background: #37393f;
}


html[data-theme="dark"]
.info-row {
  border-top-color:
    rgba(
      255,
      255,
      255,
      0.06
    );
}


html[data-theme="dark"]
.feed-footer {
  background: #1e1f22;
}


/* ============================================================
   TABLET
============================================================ */

@media (
  max-width: 900px
) {

  .event-scrollable-content {
    padding:
      32px
      18px
      60px;
  }


  .event-hero {
    height: 420px;
  }


  .event-layout {
    grid-template-columns:
      1fr;
  }


  .sidebar-sticky {
    position: static;

    display: grid;

    grid-template-columns:
      1fr
      1fr;
  }

}


/* ============================================================
   MOBILE
============================================================ */

@media (
  max-width: 600px
) {

  .event-scrollable-content {
    padding:
      18px
      10px
      40px;
  }


  .event-hero {
    height: 360px;

    border-radius: 20px;
  }


  .hero-content {
    left: 20px;
    right: 20px;
    bottom: 22px;
  }


  .hero-content h1 {
    font-size: 27px;

    line-height: 1.14;
  }


  .hero-preview-title {
    font-size: 11px;
  }


  .hero-date {
    top: 14px;
    right: 14px;

    min-width: 55px;
    height: 55px;

    border-radius: 15px;
  }


  .hero-date strong {
    font-size: 19px;
  }


  .hero-location {
    top: 14px;
    left: 14px;

    max-width:
      calc(
        100% - 90px
      );
  }


  .event-layout {
    margin-top: 14px;

    gap: 14px;
  }


  .event-main {
    gap: 14px;
  }


  .organizer-card {
    padding: 15px;

    border-radius: 16px;
  }


  .organizer-logo {
    width: 47px;
    height: 47px;

    border-radius: 13px;
  }


  .content-section {
    padding:
      21px
      18px;

    border-radius: 17px;
  }


  .event-description {
    font-size: 13px;

    line-height: 1.7;
  }


  .sidebar-sticky {
    grid-template-columns:
      1fr;

    gap: 14px;
  }


  .participants-card {
    align-items: flex-start;

    flex-direction: column;
  }


  .avatars {
    padding-left: 0;
  }


  .event-button {
    min-height: 47px;

    font-size: 10px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (
  prefers-reduced-motion:
  reduce
) {

  .event-button {
    transition: none;
  }

}

</style>